import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaArrowRight,
  FaCheckCircle,
  FaPaperPlane,
  FaSpinner,
} from 'react-icons/fa';
import { FaLinkedinIn } from 'react-icons/fa6';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import './Contact.css';

/* ─────────────────────────────────────
   STATIC DATA
───────────────────────────────────── */

const CONTACT_DETAILS = [
  {
    icon:  <FaEnvelope size={18} />,
    label: 'Email Us',
    value: (
      <a href="mailto:info@nuclearsocietyofkenya.org">
        info@nuclearsocietyofkenya.org
      </a>
    ),
  },
  {
    icon:  <FaMapMarkerAlt size={18} />,
    label: 'Location',
    value: 'Nairobi, Kenya',
  },
  {
    icon:  <FaLinkedinIn size={18} />,
    label: 'LinkedIn',
    value: (
      <a
        href="https://www.linkedin.com/company/nuclear-society-of-kenya"
        target="_blank"
        rel="noopener noreferrer"
      >
        Nuclear Society of Kenya
      </a>
    ),
  },
  {
    icon:  <FaClock size={18} />,
    label: 'Response Time',
    value: 'Within 2 business days',
  },
];

const QUICK_LINKS = [
  { label: 'Apply for Membership', to: '/membership', external: false },
  { label: 'Learn About NSK',      to: '/about',      external: false },
  {
    label: 'Partnership Enquiry',
    to:    'mailto:info@nuclearsocietyofkenya.org?subject=Partnership%20Enquiry',
    external: true,
  },
];

const SUBJECTS = [
  'General Enquiry',
  'Membership Application',
  'Event Information',
  'Partnership & Sponsorship',
  'Media & Press',
  'Research Collaboration',
  'Other',
];

/* ─────────────────────────────────────
   VALIDATION
───────────────────────────────────── */

const EMPTY_FORM = {
  firstName: '',
  lastName:  '',
  email:     '',
  subject:   '',
  message:   '',
  honeypot:  '',
};

function validate(fields) {
  const errs = {};
  if (!fields.firstName.trim())
    errs.firstName = 'First name is required.';
  if (!fields.email.trim())
    errs.email = 'Email address is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
    errs.email = 'Please enter a valid email address.';
  if (!fields.subject)
    errs.subject = 'Please select a subject.';
  if (!fields.message.trim())
    errs.message = 'Message cannot be empty.';
  else if (fields.message.trim().length < 20)
    errs.message = 'Please write at least 20 characters.';
  return errs;
}

/* ─────────────────────────────────────
   SUCCESS STATE
───────────────────────────────────── */

function SuccessMessage({ onReset }) {
  return (
    <div className="contact-success" role="alert" aria-live="polite">
      <div className="contact-success__icon" aria-hidden="true">
        <FaCheckCircle size={36} />
      </div>
      <h2 className="contact-success__title">Message Sent</h2>
      <p className="contact-success__text">
        Thank you for reaching out. A member of the NSK team will get back
        to you within 2 business days.
      </p>
      <button
        className="btn btn--green contact-success__back"
        onClick={onReset}
      >
        Send Another Message
      </button>
    </div>
  );
}

/* ─────────────────────────────────────
   CONTACT PAGE
───────────────────────────────────── */

function Contact() {
  const [fields, setFields] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
    }
  }, [errors]);

  const handleSubmit = useCallback(async () => {
    if (fields.honeypot) return;

    const errs = validate(fields);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      document.getElementById(`cf-${Object.keys(errs)[0]}`)?.focus();
      return;
    }

    setStatus('submitting');

    try {
      await addDoc(collection(db, 'contacts'), {
        firstName:   fields.firstName,
        lastName:    fields.lastName,
        email:       fields.email,
        subject:     fields.subject,
        message:     fields.message,
        submittedAt: serverTimestamp(),
        read:        false,
      });
      setStatus('success');
    } catch (error) {
      console.error('Firestore error:', error);
      setErrors({
        submit: 'Something went wrong. Please email us directly at info@nuclearsocietyofkenya.org',
      });
      setStatus('idle');
    }
  }, [fields]);

  const handleReset = useCallback(() => {
    setFields(EMPTY_FORM);
    setErrors({});
    setStatus('idle');
  }, []);

  return (
    <>
      {/* ── Page Hero ── */}
      <section className="contact-hero" aria-labelledby="contact-hero-heading">
        <div className="contact-hero__inner">
          <nav className="contact-hero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="contact-hero__breadcrumb-sep" aria-hidden="true">›</span>
            <span>Contact</span>
          </nav>
          <h1 className="contact-hero__title" id="contact-hero-heading">
            Get in Touch
          </h1>
          <p className="contact-hero__lead">
            Have a question about membership, events, or partnerships?
            We'd love to hear from you — our team typically responds
            within 2 business days.
          </p>
        </div>
      </section>

      {/* ── Main Body ── */}
      <section className="contact-body" aria-label="Contact information and form">
        <div className="contact-body__inner">

          {/* ── Left: Info Panel ── */}
          <aside aria-label="Contact details">
            <div className="contact-info">
              <div className="contact-info__card">
                <h2 className="contact-info__heading">
                  Contact the Nuclear Society of Kenya
                </h2>

                {CONTACT_DETAILS.map(({ icon, label, value }) => (
                  <div className="contact-detail" key={label}>
                    <div className="contact-detail__icon" aria-hidden="true">
                      {icon}
                    </div>
                    <div>
                      <p className="contact-detail__label">{label}</p>
                      <p className="contact-detail__value">{value}</p>
                    </div>
                  </div>
                ))}

                <div className="contact-info__response" aria-label="Current response status">
                  <span className="contact-info__response-dot" aria-hidden="true" />
                  We are currently responding within 2 business days
                </div>
              </div>

              {/* Quick links card */}
              <div className="contact-quicklinks">
                <p className="contact-quicklinks__heading">Quick Links</p>
                {QUICK_LINKS.map(({ label, to, external }) =>
                  external ? (
                    <a
                      key={label}
                      href={to}
                      className="contact-quicklinks__link"
                      {...(to.startsWith('mailto')
                        ? {}
                        : { target: '_blank', rel: 'noopener noreferrer' })}
                    >
                      {label}
                      <FaArrowRight size={12} className="contact-quicklinks__arrow" aria-hidden="true" />
                    </a>
                  ) : (
                    <Link
                      key={label}
                      to={to}
                      className="contact-quicklinks__link"
                    >
                      {label}
                      <FaArrowRight size={12} className="contact-quicklinks__arrow" aria-hidden="true" />
                    </Link>
                  )
                )}
              </div>
            </div>
          </aside>

          {/* ── Right: Form ── */}
          <div className="contact-form-wrap">
            {status === 'success' ? (
              <SuccessMessage onReset={handleReset} />
            ) : (
              <>
                <h2 className="contact-form-wrap__heading">Send Us a Message</h2>
                <p className="contact-form-wrap__sub">
                  Fields marked{' '}
                  <span aria-label="required" style={{ color: 'var(--color-danger)' }}>*</span>
                  {' '}are required.
                </p>

                <div className="cform" role="group" aria-label="Contact form">

                  {/* Honeypot — hidden spam trap */}
                  <div className="cform__honeypot" aria-hidden="true">
                    <label htmlFor="cf-website">Website</label>
                    <input
                      id="cf-website"
                      name="honeypot"
                      type="text"
                      value={fields.honeypot}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Name row */}
                  <div className="cform__row">
                    <div className="cform__field">
                      <label
                        htmlFor="cf-firstName"
                        className="cform__label cform__label--required"
                      >
                        First Name
                      </label>
                      <input
                        id="cf-firstName"
                        name="firstName"
                        type="text"
                        className={`cform__input${errors.firstName ? ' error' : ''}`}
                        value={fields.firstName}
                        onChange={handleChange}
                        autoComplete="given-name"
                        aria-required="true"
                        aria-describedby={errors.firstName ? 'err-firstName' : undefined}
                        aria-invalid={!!errors.firstName}
                      />
                      {errors.firstName && (
                        <span id="err-firstName" className="cform__error" role="alert">
                          {errors.firstName}
                        </span>
                      )}
                    </div>

                    <div className="cform__field">
                      <label htmlFor="cf-lastName" className="cform__label">
                        Last Name
                      </label>
                      <input
                        id="cf-lastName"
                        name="lastName"
                        type="text"
                        className="cform__input"
                        value={fields.lastName}
                        onChange={handleChange}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="cform__field">
                    <label
                      htmlFor="cf-email"
                      className="cform__label cform__label--required"
                    >
                      Email Address
                    </label>
                    <input
                      id="cf-email"
                      name="email"
                      type="email"
                      className={`cform__input${errors.email ? ' error' : ''}`}
                      value={fields.email}
                      onChange={handleChange}
                      autoComplete="email"
                      aria-required="true"
                      aria-describedby={errors.email ? 'err-email' : undefined}
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && (
                      <span id="err-email" className="cform__error" role="alert">
                        {errors.email}
                      </span>
                    )}
                  </div>

                  {/* Subject */}
                  <div className="cform__field">
                    <label
                      htmlFor="cf-subject"
                      className="cform__label cform__label--required"
                    >
                      Subject
                    </label>
                    <select
                      id="cf-subject"
                      name="subject"
                      className={`cform__select${errors.subject ? ' error' : ''}`}
                      value={fields.subject}
                      onChange={handleChange}
                      aria-required="true"
                      aria-describedby={errors.subject ? 'err-subject' : undefined}
                      aria-invalid={!!errors.subject}
                    >
                      <option value="" disabled>Select a subject…</option>
                      {SUBJECTS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {errors.subject && (
                      <span id="err-subject" className="cform__error" role="alert">
                        {errors.subject}
                      </span>
                    )}
                  </div>

                  {/* Message */}
                  <div className="cform__field">
                    <label
                      htmlFor="cf-message"
                      className="cform__label cform__label--required"
                    >
                      Message
                    </label>
                    <textarea
                      id="cf-message"
                      name="message"
                      className={`cform__textarea${errors.message ? ' error' : ''}`}
                      value={fields.message}
                      onChange={handleChange}
                      rows={6}
                      aria-required="true"
                      aria-describedby={errors.message ? 'err-message' : undefined}
                      aria-invalid={!!errors.message}
                    />
                    {errors.message && (
                      <span id="err-message" className="cform__error" role="alert">
                        {errors.message}
                      </span>
                    )}
                  </div>

                  {/* Submit error banner */}
                  {errors.submit && (
                    <div role="alert" className="cform__submit-error">
                      {errors.submit}
                    </div>
                  )}

                  {/* Submit row */}
                  <div className="cform__submit-row">
                    <p className="cform__privacy">
                      Your details are kept private and used only to respond
                      to your enquiry.
                    </p>
                    <button
                      type="button"
                      className="cform__submit"
                      onClick={handleSubmit}
                      disabled={status === 'submitting'}
                      aria-busy={status === 'submitting'}
                    >
                      {status === 'submitting' ? (
                        <>
                          <FaSpinner size={14} aria-hidden="true"
                            style={{ animation: 'spin 1s linear infinite' }} />
                          Sending…
                        </>
                      ) : (
                        <>
                          <FaPaperPlane size={14} aria-hidden="true" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>

                </div>
              </>
            )}
          </div>

        </div>
      </section>
    </>
  );
}

export default Contact;